import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    MDBAlert,
    MDBBtn,
    MDBDatatable,
    MDBIcon,
    MDBInput,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalFooter,
    MDBModalHeader,
    MDBModalTitle,
    MDBSpinner,
    MDBTable,
    MDBTableBody,
    MDBTableHead,
} from "mdb-react-ui-kit";
import Body from "../../components/body/BodyPage";
import MainLinks from "../../components/navlinks/MainLinks";
import { getAllStreamsLogins, getSpecificStreamsLogins, forceLogoutStreams } from "../../apiclients/streamsapi_bridge";

// ── Device-type icon ──────────────────────────────────────────────────────────
function DeviceIcon({ label = "" }) {
    const t = label.toLowerCase();
    const isMobile =
        t.includes("mobile") || t.includes("android") ||
        t.includes("ios")    || t.includes("iphone");
    return (
        <i className={`${isMobile ? "fas fa-mobile-alt" : "fas fa-desktop"} streams-device-icon`} />
    );
}

function StreamsManagerLanding() {
    const siteid = JSON.parse(sessionStorage.getItem("admin_session"))?.siteid;

    // ── Main list ────────────────────────────────────────────────────────────
    const [userList,    setUserList]    = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [isloading,   setIsloading]   = useState(true);
    const loadingTimerRef               = useRef(null);

    // ── Search ───────────────────────────────────────────────────────────────
    const [searchValue, setSearchValue] = useState("");

    // ── Device-sessions modal ────────────────────────────────────────────────
    const [showModal,      setShowModal]      = useState(false);
    const [selectedUser,   setSelectedUser]   = useState(null);
    const [deviceSessions, setDeviceSessions] = useState([]);
    const [loadingModal,   setLoadingModal]   = useState(false);
    const [selectedIds,    setSelectedIds]    = useState([]);

    // ── Confirmation dialog ──────────────────────────────────────────────────
    const [showConfirm,   setShowConfirm]   = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);

    // ── Toasts ───────────────────────────────────────────────────────────────
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg,   setErrorMsg]   = useState("");

    // ── Toast helpers ────────────────────────────────────────────────────────
    const showSuccess = (msg) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(""), 10000);
    };

    const showError = (msg) => {
        setErrorMsg(msg);
        setTimeout(() => setErrorMsg(""), 10000);
    };

    // ── Fetch user list ──────────────────────────────────────────────────────
    const fetchUserList = useCallback(async () => {
        if (!siteid) return;
        if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
        setLoadingList(true);
        try {
            const resp = await getAllStreamsLogins(siteid);
            if (resp?.ok) {
                const json = await resp.json();
                setUserList(json?.data || []);
            } else {
                setErrorMsg("Failed to load device manager data.");
            }
        } catch (e) {
            console.error("StreamsManagerLanding fetchUserList:", e);
            setErrorMsg("Failed to load device manager data.");
        } finally {
            setLoadingList(false);
            loadingTimerRef.current = setTimeout(() => setIsloading(false), 2000);
        }
    }, [siteid]);

    useEffect(() => { fetchUserList(); }, [fetchUserList]);

    // ── Fetch device sessions ─────────────────────────────────────────────────
    const fetchDeviceSessions = useCallback(async (agentid) => {
        setLoadingModal(true);
        try {
            const resp = await getSpecificStreamsLogins(siteid, agentid);
            if (resp?.ok) {
                const json = await resp.json();
                setDeviceSessions(json?.data || []);
            } else {
                let errMsg = "Failed to load device sessions.";
                try {
                    const json = await resp.json();
                    const first = json?.message?.errors?.[0]?.message || json?.message;
                    if (first && typeof first === "string") errMsg = first;
                } catch (_) {}
                showError(errMsg);
                setShowModal(false);
            }
        } catch (e) {
            console.error("StreamsManagerLanding fetchDeviceSessions:", e);
            showError("Failed to load device sessions.");
            setShowModal(false);
        } finally {
            setLoadingModal(false);
        }
    }, [siteid]);

    // ── Open / close modal ───────────────────────────────────────────────────
    const openUserModal = (user) => {
        setSelectedUser(user);
        setDeviceSessions([]);
        setSelectedIds([]);
        setShowModal(true);
        fetchDeviceSessions(user.agentid);
    };

    // Refresh landing page only on close (req #6)
    const closeModal = () => {
        if (logoutLoading) return;
        setShowModal(false);
        setSelectedUser(null);
        setDeviceSessions([]);
        setSelectedIds([]);
        setIsloading(true);
        fetchUserList();
    };

    // ── Checkbox helpers ─────────────────────────────────────────────────────
    const toggleSelectAll = () => {
        setSelectedIds(
            selectedIds.length === deviceSessions.length
                ? []
                : deviceSessions.map(s => s.uniqueId)
        );
    };

    const toggleSelect = (uniqueId) => {
        setSelectedIds(prev =>
            prev.includes(uniqueId)
                ? prev.filter(id => id !== uniqueId)
                : [...prev, uniqueId]
        );
    };

    // ── Multi-logout flow ────────────────────────────────────────────────────
    const handleLogoutClick = () => {
        if (selectedIds.length === 0) return;
        setShowConfirm(true);
    };

    const handleConfirmLogout = async () => {
        setShowConfirm(false);
        setLogoutLoading(true);
        const loggedOutIds = [...selectedIds];
        const islogoffall  = loggedOutIds.length === deviceSessions.length ? 1 : 0;
        try {
            const resp = await forceLogoutStreams(selectedUser.agentid, loggedOutIds, islogoffall, siteid);
            if (!resp?.ok) {
                let errMsg = "Force logout failed. Please try again.";
                try {
                    const json = await resp.json();
                    const first = json?.message?.errors?.[0]?.message || json?.message;
                    if (first && typeof first === "string") errMsg = first;
                } catch (_) {}
                showError(errMsg);
                return;
            }
            // Optimistic update: remove logged-out rows immediately (req #1)
            setDeviceSessions(prev => prev.filter(s => !loggedOutIds.includes(s.uniqueId)));
            setSelectedIds([]);
            showSuccess("Selected device(s) have been successfully logged out.");
        } catch (e) {
            console.error("StreamsManagerLanding handleConfirmLogout:", e);
            showError("Force logout failed. Please try again.");
        } finally {
            setLogoutLoading(false);
        }
    };

    // ── Datatable definition ─────────────────────────────────────────────────
    const filteredList = searchValue.trim()
        ? userList.filter(u => {
            const q = searchValue.toLowerCase();
            return (
                (u.name    || u.agentid || "").toLowerCase().includes(q) ||
                (u.emailId || "").toLowerCase().includes(q) ||
                String(u.numberOfDevices ?? "").includes(q)
            );
          })
        : userList;

    const datatableData = {
        columns: [
            { label: "Name",              field: "name",       sort: true },
            { label: "Email ID",          field: "email",      sort: true },
            { label: "Number of Devices", field: "loginCount", sort: true },
        ],
        rows: filteredList.map((user) => {
            const displayName = user.name || user.agentid;
            const nameCell = Object.assign({}, (
                <span className="d-inline-flex align-items-center">
                    <span className="streams-user-avatar">
                        <i className="fas fa-user" style={{ fontSize: 13, color: "#777" }} />
                    </span>
                    {displayName}
                </span>
            ));
            nameCell.valueOf = () => displayName.toLowerCase();
            return {
                userInfo:   user,
                name:       nameCell,
                email:      user.emailId,
                loginCount: user.numberOfDevices ?? 0,
            };
        }),
    };

    const userName  = selectedUser ? selectedUser.name || selectedUser.agentid : "";
    const allChecked = deviceSessions.length > 0 && selectedIds.length === deviceSessions.length;

    return (
        <React.Fragment>
            <MainLinks active={3} />
            <Body
                tablinks={3}
                content={
                    <div className="card card-height">

                        {/* ── Page header ── */}
                        <div className="card-header py-3 px-0">
                            <h5 className="mb-0">Device Manager</h5>
                        </div>

                        {/* ── Page loader ── */}
                        {isloading && (
                            <div className="loadingclass">
                                <MDBBtn disabled>
                                    <MDBSpinner size="sm" role="status" tag="span" className="me-2" />
                                    Loading...
                                </MDBBtn>
                            </div>
                        )}

                        {/* ── User-list table ── */}
                        <div className="card-body px-0 streams-card-body">
                            {loadingList ? (
                                <div className="d-flex justify-content-center py-5">
                                    <MDBSpinner role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </MDBSpinner>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-2 mt-3 position-relative streams-search-wrap">
                                        <MDBInput
                                            label="Search"
                                            value={searchValue}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                        />
                                        <MDBIcon fas icon="search" className="MDBDatatable-Search" />
                                    </div>
                                    <div className="streams-table-wrap">
                                        <MDBDatatable
                                            key={searchValue}
                                            data={datatableData}
                                            hover
                                            fixedHeader
                                            sm
                                            noFoundMessage="No users found."
                                            onRowClick={(e) => openUserModal(e.userInfo)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* ════════════════════════════════════════════════════
                            Device-sessions modal
                        ════════════════════════════════════════════════════ */}
                        {/* staticBackdrop prevents closing on outside click (req #4) */}
                        <MDBModal open={showModal} onClose={closeModal} staticBackdrop tabIndex="-1">
                            <MDBModalDialog size="lg" centered>
                                <MDBModalContent className="position-relative">

                                    {loadingModal && (
                                        <div className="loadingclass">
                                            <MDBBtn disabled>
                                                <MDBSpinner size="sm" role="status" tag="span" className="me-2" />
                                                Loading...
                                            </MDBBtn>
                                        </div>
                                    )}

                                    <MDBModalHeader>
                                        <MDBModalTitle>
                                            Multi Device Manager{" "}
                                            {userName && (
                                                <span className="streams-modal-username">({userName})</span>
                                            )}
                                        </MDBModalTitle>
                                        <MDBBtn className="btn-close" color="none" onClick={closeModal} />
                                    </MDBModalHeader>

                                    <MDBModalBody className="streams-modal-body">
                                        {!loadingModal && deviceSessions.length === 0 ? (
                                            /* req #5 — no devices after full logout */
                                            <p className="text-center text-muted py-3">No devices found</p>
                                        ) : (
                                            <div className="position-relative">

                                                {/* ── Logout-in-progress overlay ── */}
                                                {logoutLoading && (
                                                    <div className="streams-logout-overlay">
                                                        <MDBBtn color="primary" disabled>
                                                            <MDBSpinner size="sm" role="status" tag="span" className="me-2" />
                                                            Loading...
                                                        </MDBBtn>
                                                    </div>
                                                )}

                                                <MDBTable hover responsive>
                                                    <MDBTableHead>
                                                        <tr>
                                                            <th style={{ width: 42 }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={allChecked}
                                                                    onChange={toggleSelectAll}
                                                                    disabled={logoutLoading}
                                                                />
                                                            </th>
                                                            <th>Device Type/OS</th>
                                                            <th>Device Version</th>
                                                            <th>Login Time</th>
                                                        </tr>
                                                    </MDBTableHead>
                                                    <MDBTableBody>
                                                        {deviceSessions.map((session, idx) => {
                                                            const checked = selectedIds.includes(session.uniqueId);
                                                            const deviceLabel = session.deviceType
                                                                ? `${session.deviceType}${session.deviceOS ? `/${session.deviceOS}` : ""}`
                                                                : session.deviceOS || "—";
                                                            return (
                                                                <tr
                                                                    key={session.uniqueId || idx}
                                                                    className={`streams-session-row${checked ? " selected" : ""}${logoutLoading ? " disabled" : ""}`}
                                                                    onClick={() => !logoutLoading && toggleSelect(session.uniqueId)}
                                                                >
                                                                    <td onClick={e => e.stopPropagation()}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={checked}
                                                                            onChange={() => !logoutLoading && toggleSelect(session.uniqueId)}
                                                                            disabled={logoutLoading}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <DeviceIcon label={session.deviceOS || session.deviceType} />
                                                                        {deviceLabel}
                                                                    </td>
                                                                    <td>{session.version || session.deviceApp || "—"}</td>
                                                                    <td>{session.loginTime || "—"}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </MDBTableBody>
                                                </MDBTable>
                                            </div>
                                        )}
                                    </MDBModalBody>

                                    <MDBModalFooter>
                                        <MDBBtn color="secondary" outline onClick={closeModal} disabled={logoutLoading}>
                                            Cancel
                                        </MDBBtn>
                                        <MDBBtn
                                            color="primary"
                                            disabled={selectedIds.length === 0 || logoutLoading}
                                            onClick={handleLogoutClick}
                                        >
                                            Logout
                                        </MDBBtn>
                                    </MDBModalFooter>

                                </MDBModalContent>
                            </MDBModalDialog>
                        </MDBModal>

                        {/* ════════════════════════════════════════════════════
                            Confirmation dialog
                        ════════════════════════════════════════════════════ */}
                        <MDBModal open={showConfirm} onClose={() => setShowConfirm(false)} tabIndex="-1">
                            <MDBModalDialog centered>
                                <MDBModalContent>
                                    <MDBModalHeader>
                                        <MDBModalTitle>Multi Device Manager</MDBModalTitle>
                                        <MDBBtn className="btn-close" color="none" onClick={() => setShowConfirm(false)} />
                                    </MDBModalHeader>
                                    <MDBModalBody>
                                        <div className="d-flex align-items-center p-3 rounded streams-confirm-warning text-dark">
                                            <i className="fas fa-exclamation-triangle me-3 streams-confirm-warn-icon" />
                                            <span className="text-dark">Are you sure you want to logout of this device(s)?</span>
                                        </div>
                                    </MDBModalBody>
                                    <MDBModalFooter>
                                        <MDBBtn color="secondary" outline onClick={() => setShowConfirm(false)}>
                                            Cancel
                                        </MDBBtn>
                                        <MDBBtn color="primary" onClick={handleConfirmLogout}>
                                            Confirm
                                        </MDBBtn>
                                    </MDBModalFooter>
                                </MDBModalContent>
                            </MDBModalDialog>
                        </MDBModal>

                    </div>
                }
            />

            {successMsg && (
                <MDBAlert open className="alert-center" color="success" autohide position="bottom-right" delay={10000}>
                    <i className="fas fa-check-circle me-3" />
                    <span className="alert-msg">{successMsg}</span>
                </MDBAlert>
            )}

            {errorMsg && (
                <MDBAlert open className="alert-center" color="danger" autohide position="bottom-right" delay={10000}>
                    <i className="fas fa-check-circle me-3" />
                    <span className="alert-msg">{errorMsg}</span>
                </MDBAlert>
            )}

        </React.Fragment>
    );
}

export default StreamsManagerLanding;
